import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';

import { apiUrls } from '../../../config/api/api-urls.config';
import { UsersResultsResponse } from '../../../models/users/responses/api-users-results-response.model';
import { UsersFiltersRequest } from '../../../models/users/requests/api-users-get-users-request.model';
import { UserTransactionsResultsResponse } from '../../../models/users/responses/api-users-transactions-results-response.model';
import { UsersTransactionsStatisticsResultsResponse } from '../../../models/users/responses/api-users-transactions-statistics-response.model';
import { UserTransactionsFiltersRequest } from '../../../models/users/requests/api-users-get-user-transactions-request.model';
import { UserMatchesFiltersRequest } from '../../../models/users/requests/api-users-get-user-matchs-request.model';
import { UserMatchesResultsResponse } from '../../../models/users/responses/api-users-matches-results-response.model';
import { UserLogsResultsResponse } from '../../../models/users/responses/api-users-logs-results-response.model';
import { UserSessionsResultsResponse } from '../../../models/users/responses/api-users-sessions-results-response.model';
import { UserLeaguesResultsResponse } from '../../../models/users/responses/api-users-leagues-results-response.model';
import { UserTournamentsResultsResponse } from '../../../models/users/responses/api-users-tournaments-results-response.model';
import { UserReportsResultsResponse } from '../../../models/users/responses/api-users-reports-results-response.model';
import { User } from '../../../models/users/user.model';
import { UserMatchDetail } from '../../../models/users/user-match-detail.model';
import { UserCollectionsResultsResponse } from '../../../models/users/responses/api-users-collections-results-response.model';
import { UserCollectionAlbumsResultsResponse } from '../../../models/users/responses/api-users-collection-albums-results-response.model';
import { UserCollectionAlbumCardsResultsResponse } from '../../../models/users/responses/api-users-collection-album-cards-results-response.model';

@Injectable({
  providedIn: 'root'
})
export class ApiUsersService {

  constructor(private apiService: ApiService) { }

  getUsers(page: number, resultCount: number, filters: UsersFiltersRequest): Promise<UsersResultsResponse> {
    return new Promise<UsersResultsResponse>((resolve, reject) => {
      const urlFull = `${apiUrls.usersGetUsersUrl}`;
      const body = { page, result_count: resultCount, filters };

      this.apiService.launchAdminApiRequestPost(urlFull, body, UsersResultsResponse).then(
        (data: UsersResultsResponse) => {
          resolve(data);
        }, error => { reject(error); });
    });
  }

  addUserCurrencyQuantity(userId: number, currencyType: number, currencyQuantity: number, currencyTicketCost: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const urlFull = `${apiUrls.usersAddCurrencyUrl}`;
      const body = { user_id: userId, currency_type: currencyType, currency_quantity: currencyQuantity, currency_ticket_cost: currencyTicketCost };

      this.apiService.launchAdminApiRequestPost(urlFull, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  addLevelExperience(userId: number, levelExperienceQuantity: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const urlFull = `${apiUrls.usersAddLevelExperienceUrl}`;
      const body = { user_id: userId, level_experience_quantity: levelExperienceQuantity };

      this.apiService.launchAdminApiRequestPost(urlFull, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editUserNickname(userId: number, nickname: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const urlFull = `${apiUrls.usersEditUserNicknameUrl}`;
      const body = { user_id: userId, nickname };

      this.apiService.launchAdminApiRequestPost(urlFull, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getUserTournaments(userId: number, page: number, resultCount: number): Promise<UserTournamentsResultsResponse> {
    return new Promise<UserTournamentsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserTournamentsUrl, body, UserTournamentsResultsResponse).then(
        (data: UserTournamentsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserLeagues(userId: number, page: number, resultCount: number): Promise<UserLeaguesResultsResponse> {
    return new Promise<UserLeaguesResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserLeaguesUrl, body, UserLeaguesResultsResponse).then(
        (data: UserLeaguesResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserSessions(userId: number, page: number, resultCount: number): Promise<UserSessionsResultsResponse> {
    return new Promise<UserSessionsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserSessionsUrl, body, UserSessionsResultsResponse).then(
        (data: UserSessionsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserLogs(userId: number, page: number, resultCount: number): Promise<UserLogsResultsResponse> {
    return new Promise<UserLogsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserLogsUrl, body, UserLogsResultsResponse).then(
        (data: UserLogsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserReports(userId: number, page: number, resultCount: number): Promise<UserReportsResultsResponse> {
    return new Promise<UserReportsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserReportsUrl, body, UserReportsResultsResponse).then(
        (data: UserReportsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserCollections(userId: number, page: number, resultCount: number): Promise<UserCollectionsResultsResponse> {
    return new Promise<UserCollectionsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserCollectionsUrl, body, UserCollectionsResultsResponse).then(
        (data: UserCollectionsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserCollectionAlbums(userId: number, collectionId: number, page: number, resultCount: number): Promise<UserCollectionAlbumsResultsResponse> {
    return new Promise<UserCollectionAlbumsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, collection_id: collectionId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserCollectionAlbumsUrl, body, UserCollectionAlbumsResultsResponse).then(
        (data: UserCollectionAlbumsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserCollectionAlbumCards(userId: number, albumId: number, page: number, resultCount: number): Promise<UserCollectionAlbumCardsResultsResponse> {
    return new Promise<UserCollectionAlbumCardsResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, album_id: albumId, page, result_count: resultCount };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserCollectionAlbumCardsUrl, body, UserCollectionAlbumCardsResultsResponse).then(
        (data: UserCollectionAlbumCardsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addUserCollectionAlbumCard(userId: number, cardId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { user_id: userId, card_id: cardId };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersAddUserCollectionAlbumCardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  removeUserCollectionAlbumCard(userId: number, cardId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { user_id: userId, card_id: cardId };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersRemoveUserCollectionAlbumCardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getUserMatch(userId: number, matchId: number): Promise<UserMatchDetail> {
    return new Promise<UserMatchDetail>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserMatchUrl, { user_id: userId, match_id: matchId }, UserMatchDetail).then(
        (data: UserMatchDetail) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserMatchs(userId: number, page: number, resultCount: number, filters: UserMatchesFiltersRequest): Promise<UserMatchesResultsResponse> {
    return new Promise<UserMatchesResultsResponse>((resolve, reject) => {
      const body = { user_id: userId, page, result_count: resultCount, filters };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersGetUserMatchsUrl, body, UserMatchesResultsResponse).then(
        (data: UserMatchesResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getUserTransactions(userId: number, page: number, resultCount: number, filters: UserTransactionsFiltersRequest): Promise<UserTransactionsResultsResponse> {
    return new Promise<UserTransactionsResultsResponse>((resolve, reject) => {
      const urlFull = `${apiUrls.usersGetUserTransactionsUrl}`;
      const body = { user_id: userId, page, result_count: resultCount, filters };

      this.apiService.launchAdminApiRequestPost(urlFull, body, UserTransactionsResultsResponse).then(
        (data: UserTransactionsResultsResponse) => {
          resolve(data);
        }, error => { reject(error); });
    });
  }

  getUsersTransactionsStatistics(): Promise<UsersTransactionsStatisticsResultsResponse> {
    return new Promise<UsersTransactionsStatisticsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestGet(apiUrls.usersGetUsersTransactionsStatisticsUrl, UsersTransactionsStatisticsResultsResponse).then(
        (data: UsersTransactionsStatisticsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addBooster(userId: number, type: number, multiplier: number, duration: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const urlFull = `${apiUrls.usersAddBoosterUrl}`;
      const body = { user_id: userId, type, multiplier, duration };

      this.apiService.launchAdminApiRequestPost(urlFull, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  restoreUser(userId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.usersRestoreUserUrl, { user_id: userId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteUser(userId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.usersDeleteUserUrl, { user_id: userId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  changeUserBanState(userId: number, isBanned: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { user_id: userId, is_banned: isBanned };

      this.apiService.launchAdminApiRequestPost(apiUrls.usersChangeUserBanStateUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getUser(userId: number): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      const urlFull = `${apiUrls.usersGetUserUrl}`;
      const body = { user_id: userId, statistics: { slot_reward: 10, slot_cost: 10, game_reward: 10, game_cost: 10 } };

      this.apiService.launchAdminApiRequestPost(urlFull, body, User).then(
        (data: User) => {
          resolve(data);
        }, error => { reject(error); });
    });
  }
}
